import { z } from 'zod';

import { isDefined, isTruthy } from '../../../utils.js';
import { ExportMode } from '../../previsit/form.js';
import {
  ScheduleAppointmentDisplayBy,
  ScheduleAppointmentSortBy,
} from '../../previsit/formTemplateFieldConfig.js';
import {
  BaseFlowBlockSchema,
  BlockType,
  RunMode,
  StringAsNumberSchema,
} from '../base/baseSchema.js';
import {
  BlockValueFieldSchema,
  BlockValueFieldSchemaVariant,
} from '../base/blockValueFieldSchema.js';
import { BlockValueTemplateDurationSchema } from '../base/blockValueTemplateDurationSchema.js';
import { EntityType } from '../flowRun.js';

export enum FormBlockSchedulingConfigsState {
  Enabled = 'Enabled',
  Disabled = 'Disabled',
}

export enum FormBlockPracticeType {
  StaticList = 'STATIC_LIST',
  Interpolated = 'INTERPOLATED',
}

export enum FormBlockProviderType {
  StaticList = 'STATIC_LIST',
  DynamicList = 'DYNAMIC_LIST',
  Interpolated = 'INTERPOLATED',
}

/**
 * Shared (unrefined) shape of the form block's config, reused by both the
 * v1 {@link FormBlockSchema} and the v2 `FormV2BlockSchema` (which extends it
 * with a `timeout` for the built-in wait).
 */
export const FormTemplateConfigSchema = z.object({
  formTemplateId: z
    .string({ required_error: 'Form template ID is required' })
    .uuid({ message: 'Form template ID must be a valid UUID' }),
  refreshInterval: BlockValueTemplateDurationSchema().optional(),
});

export const FormBlockBaseConfigShape = z.object({
  formTemplateConfigs: z
    .array(FormTemplateConfigSchema)
    .min(1, 'Form template configuration is required')
    .optional(),
  runMode: z.nativeEnum(RunMode).optional().default(RunMode.Live),
  exportMode: z.nativeEnum(ExportMode).optional().default(ExportMode.RunAutoExport),
  dynamicParams: z
    .array(
      z
        .object({
          key: z.string().min(1, { message: 'Key cannot be empty' }),
          value: z.string().min(1, { message: 'Value cannot be empty' }),
        })
        .required()
    )
    .refine((dynamicParams) => {
      const keys = dynamicParams.map((param) => param.key);
      return keys.length === new Set(keys).size;
    }, 'The same dynamic parameter key cannot be used twice.')
    .optional(),
  schedulingConfigs: z
    .object({
      schedulingConfigsState: z.nativeEnum(FormBlockSchedulingConfigsState),
      fhirAppointmentKinds: z.array(z.string()).optional(),
      practiceType: z.nativeEnum(FormBlockPracticeType),
      practiceIds: z.array(z.string()).optional(),
      interpolatedPractice: BlockValueFieldSchema({
        variant: BlockValueFieldSchemaVariant.OptionalTemplate,
      }).optional(),
      providerType: z.nativeEnum(FormBlockProviderType),
      providerIds: z.array(z.string()).optional(),
      fhirDynamicProviders: z.array(z.string()).optional(),
      interpolatedProvider: BlockValueFieldSchema({
        variant: BlockValueFieldSchemaVariant.OptionalTemplate,
      }).optional(),
      fhirTotalResult: StringAsNumberSchema(z.number().int().min(1).optional()),
      scheduleAppointmentDisplayBy: z.nativeEnum(ScheduleAppointmentDisplayBy).optional(),
      scheduleAppointmentSortBy: z.nativeEnum(ScheduleAppointmentSortBy).optional(),
      scheduleAppointmentStartTime: BlockValueTemplateDurationSchema().optional(),
      scheduleAppointmentEndTime: BlockValueTemplateDurationSchema().optional(),
      scheduleAppointmentCallSectionEnabled: z.boolean(),
      scheduleAppointmentCommentEnabled: z.boolean(),
      scheduleAppointmentDefaultReasonForVisit: z.string().optional(),
      customConfigs: z
        .object({
          scheduleAppointmentSeriesMinTime: BlockValueTemplateDurationSchema().optional(),
          scheduleAppointmentSeriesMaxTime: BlockValueTemplateDurationSchema().optional(),
          appointmentCommentModalLabel: z.string().optional(),
          appointmentCommentModalPlaceholder: z.string().optional(),
          appointmentCommentModalTitle: z.string().optional(),
          scheduleNextAvailableAppointmentEnabled: z.boolean().optional(),
          useReasonForVisitFromPatientOrders: z.boolean().optional(),
        })
        .optional(),
    })
    .optional(),
  isOverridingEntity: z.boolean().optional(),
  entityOverride: z
    .object({
      entityType: z.nativeEnum(EntityType),
      entityId: z.string(),
    })
    .optional(),
});

export function formBlockEntityOverrideCheck(config: {
  isOverridingEntity?: boolean;
  entityOverride?: { entityType: EntityType; entityId: string };
}): boolean {
  if (!config.isOverridingEntity) {
    return true; // Everything is ok
  }

  // We're overriding the entity, make sure it's specified
  const entity = config.entityOverride;
  if (!isDefined(entity)) {
    return false;
  }

  return isTruthy(entity.entityType) && isTruthy(entity.entityId);
}

export const formBlockEntityOverrideRefineMessage = {
  message: 'When overriding the entity, both entityType and entityId must be specified.',
  path: ['entityOverride', 'entityType'],
};

export const FormBlockConfigSchema = FormBlockBaseConfigShape.refine(
  formBlockEntityOverrideCheck,
  formBlockEntityOverrideRefineMessage
);
export type FormBlockConfig = z.infer<typeof FormBlockConfigSchema>;

export const FormBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.Form),
  config: FormBlockConfigSchema,
});
export type FormBlock = z.infer<typeof FormBlockSchema>;
